/**
 * Google Tag Manager initialization utility
 * Dynamically injects GTM script and noscript iframe based on environment variable
 * Follows Google's official GTM implementation pattern
 */

export const initializeGTM = (): void => {
  const gtmId = import.meta.env.VITE_GTM_ID;

  // Only initialize if GTM ID is set
  if (!gtmId || gtmId.trim() === '') {
    return;
  }

  // Initialize dataLayer and push GTM start event (must happen before script loads)
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Inject GTM script following Google's official pattern
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

  // Insert script in head, before first script tag if available
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  // Inject noscript iframe (for users with JavaScript disabled)
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);

  // Insert noscript at the beginning of body
  if (document.body) {
    document.body.insertBefore(noscript, document.body.firstChild);
  }
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}
